import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import { ReceiptImageViewer } from "../components/ReceiptImageViewer";
import { SiblingNav } from "../components/SiblingNav";
import { formatDate, formatMoney, isMatchReady } from "../lib/format";
import { loadReceiptMedia } from "../lib/receiptMedia";
import { useAuth } from "../store/auth";
import type { PendingReceiptOut, Receipt, ReceiptUploadResponse } from "../types";

export function ReceiptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = useAuth((s) => s.user?.role);
  const backTo = role === "admin" ? "/admin" : "/";
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [pending, setPending] = useState<PendingReceiptOut | null>(null);
  const [siblingIds, setSiblingIds] = useState<number[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const closeViewer = useCallback(() => setViewerOpen(false), []);

  useEffect(() => {
    let cancelled = false;
    let revoke: (() => void) | undefined;

    (async () => {
      try {
        setPreviewUrl(null);
        setFileUrl(null);
        setViewerOpen(false);
        setPending(null);
        setError(null);

        const r = await api.request<Receipt>(`/receipts/${id}`);
        if (cancelled) return;
        setReceipt(r);

        const pdf =
          (r.content_type || "").toLowerCase().includes("pdf") ||
          r.original_filename.toLowerCase().endsWith(".pdf");
        setIsPdf(pdf);

        const fromState = (location.state as { siblingIds?: number[] } | null)
          ?.siblingIds;
        const list = await api.request<PendingReceiptOut[]>(
          "/receipts?pending_only=true&include_suggestions=true",
        );
        if (cancelled) return;
        let ids =
          fromState && fromState.length > 0
            ? [...fromState]
            : list.map((p) => p.receipt.id);
        if (!ids.includes(r.id)) ids = [r.id, ...ids];
        setSiblingIds(ids);
        setPending(list.find((p) => p.receipt.id === r.id) ?? null);

        const media = await loadReceiptMedia(r.id, pdf);
        if (cancelled) {
          media.revoke();
          return;
        }
        revoke = media.revoke;
        setPreviewUrl(media.previewUrl);
        setFileUrl(media.fileUrl);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Receipt not found");
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke?.();
    };
  }, [id, location.state]);

  function goSibling(nextId: number) {
    navigate(`/receipts/${nextId}`, { state: { siblingIds } });
  }

  function rememberUpload(fresh: ReceiptUploadResponse) {
    const raw = JSON.stringify(fresh);
    sessionStorage.setItem("lastUpload", raw);
    try {
      localStorage.setItem("lastUpload", raw);
    } catch {
      /* ignore */
    }
  }

  async function openMatch() {
    if (!receipt) return;
    setBusy(true);
    setError(null);
    try {
      const fresh = await api.request<ReceiptUploadResponse>(
        `/receipts/${receipt.id}/rematch`,
        { method: "POST" },
      );
      rememberUpload(fresh);
      navigate("/match");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open match");
    } finally {
      setBusy(false);
    }
  }

  async function overrule() {
    if (!receipt) return;
    const txnId = receipt.already_matched_transaction_id;
    const kept = txnId
      ? `transaction #${txnId}`
      : "the receipt already on file";
    if (
      !confirm(
        `This looks like the receipt already on ${kept}. Overrule that and match this copy to another card line? The existing match stays.`,
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const fresh = await api.request<ReceiptUploadResponse>(
        `/receipts/${receipt.id}/overrule`,
        { method: "POST" },
      );
      rememberUpload(fresh);
      navigate("/match");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not overrule");
      setBusy(false);
    }
  }

  async function remove() {
    if (!receipt || !confirm("Delete this pending receipt?")) return;
    setBusy(true);
    try {
      const idx = siblingIds.indexOf(receipt.id);
      const after = siblingIds.filter((sid) => sid !== receipt.id);
      const goTo = after[idx] ?? after[idx - 1] ?? null;
      await api.request(`/receipts/${receipt.id}`, { method: "DELETE" });
      if (goTo != null) {
        navigate(`/receipts/${goTo}`, { state: { siblingIds: after } });
      } else {
        navigate(backTo);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  if (error && !receipt) {
    return (
      <div className="app-shell">
        <div className="alert">{error}</div>
        <Link to={backTo} className="btn btn-ghost btn-block">
          Back
        </Link>
        <BottomNav />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="app-shell">
        <p className="muted">Loading…</p>
        <BottomNav />
      </div>
    );
  }

  const extraction = pending?.extraction;
  const suggestions = pending?.suggestions ?? [];
  const ready = isMatchReady(suggestions);
  const hasSuggestion = suggestions.length > 0;
  const alreadyTxn = receipt.already_matched_transaction_id ?? null;
  const alreadyReceipt = receipt.already_matched_receipt_id ?? null;
  const statusLabel =
    receipt.match_status === "matched"
      ? "Matched"
      : alreadyTxn
        ? "Already matched"
        : receipt.match_status === "duplicate" || receipt.duplicate_of_id
          ? "Duplicate"
          : ready
            ? "Match ready"
            : hasSuggestion || receipt.match_status === "suggested"
              ? "Possible match"
              : "Waiting for CSV";
  const duplicateOf = receipt.duplicate_of_id;

  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to={backTo} className="muted">
          ← Back
        </Link>
        <span
          className={
            ready ? "badge badge-suggested" : "badge badge-unmatched"
          }
        >
          {statusLabel}
        </span>
      </header>

      <SiblingNav
        currentId={receipt.id}
        siblingIds={siblingIds}
        onGo={goSibling}
        busy={busy}
      />

      <div className="panel stack" style={{ padding: 0, overflow: "hidden" }}>
        <div className="receipt-viewer">
          {!previewUrl ? (
            <p className="muted" style={{ padding: "2rem", textAlign: "center" }}>
              Loading file…
            </p>
          ) : (
            <img
              src={previewUrl}
              alt="Receipt"
              className="receipt-image"
            />
          )}
        </div>
        {isPdf && previewUrl && (
          <div className="muted" style={{ padding: "0.55rem 1rem", fontSize: "0.8rem" }}>
            PDF preview (first page)
          </div>
        )}
      </div>

      <div className="panel stack" style={{ marginTop: "0.85rem" }}>
        <div className="row between">
          <strong>
            {extraction?.merchant ||
              receipt.extracted_merchant ||
              receipt.original_filename}
          </strong>
          <span className="amount">
            {extraction?.amount != null
              ? formatMoney(String(extraction.amount), extraction.currency || "EUR")
              : receipt.extracted_amount != null
                ? formatMoney(
                    String(receipt.extracted_amount),
                    receipt.extracted_currency || "EUR",
                  )
                : "—"}
          </span>
        </div>
        <div className="muted" style={{ fontSize: "0.9rem" }}>
          {(extraction?.date
            ? formatDate(String(extraction.date))
            : receipt.extracted_date
              ? formatDate(String(receipt.extracted_date))
              : formatDate(receipt.created_at))}
          {(extraction?.time || receipt.extracted_time)
            ? ` · ${extraction?.time || receipt.extracted_time}`
            : ""}
          {alreadyTxn
            ? ` · Already on transaction #${alreadyTxn}`
            : suggestions.length > 0
              ? ` · ${suggestions.length} suggestion(s)`
              : " · No card line yet"}
        </div>
        {(extraction?.reference ||
          receipt.extracted_reference ||
          extraction?.invoice_number ||
          extraction?.order_number ||
          extraction?.ticket_number ||
          extraction?.authorization_code) && (
          <div className="muted mono" style={{ fontSize: "0.8rem" }}>
            Ref{" "}
            {extraction?.reference ||
              receipt.extracted_reference ||
              extraction?.invoice_number ||
              extraction?.order_number ||
              extraction?.ticket_number ||
              extraction?.authorization_code}
          </div>
        )}
        {(extraction?.payment_method ||
          receipt.extracted_payment_hint ||
          extraction?.address) && (
          <div className="muted" style={{ fontSize: "0.85rem" }}>
            {[
              extraction?.payment_method || receipt.extracted_payment_hint,
              extraction?.card_last4 ? `••${extraction.card_last4}` : null,
              extraction?.address,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
        <div className="muted mono" style={{ fontSize: "0.75rem" }}>
          {receipt.original_filename} · #{receipt.id}
        </div>
        {error && <div className="alert">{error}</div>}

        {alreadyTxn ? (
          <div className="alert" style={{ margin: 0 }}>
            This looks like scan #{alreadyReceipt ?? "—"}, already matched to
            transaction #{alreadyTxn}. Overrule if this copy is a different charge.
            The existing match stays.
            <div className="stack" style={{ marginTop: "0.65rem" }}>
              <button
                type="button"
                className="btn btn-primary btn-block"
                disabled={busy}
                onClick={overrule}
              >
                {busy ? "Opening…" : "Overrule — match this copy"}
              </button>
              <Link
                className="btn btn-ghost btn-block"
                to={`/transactions/${alreadyTxn}`}
              >
                Open transaction #{alreadyTxn}
              </Link>
              {alreadyReceipt && (
                <Link
                  className="btn btn-ghost btn-block"
                  to={`/receipts/${alreadyReceipt}`}
                >
                  Open matched scan #{alreadyReceipt}
                </Link>
              )}
            </div>
          </div>
        ) : null}

        {duplicateOf && !alreadyTxn ? (
          <div className="alert" style={{ margin: 0 }}>
            This is an extra copy of the same receipt (#{duplicateOf}), which is
            already on file. Delete this copy — do not match it to another card line.
            <div style={{ marginTop: "0.65rem" }}>
              <Link className="btn btn-primary btn-block" to={`/receipts/${duplicateOf}`}>
                Open original scan #{duplicateOf}
              </Link>
            </div>
          </div>
        ) : null}

        {receipt.match_status !== "matched" && !duplicateOf && !alreadyTxn && (
          <button
            type="button"
            className="btn btn-accent btn-block"
            disabled={busy}
            onClick={openMatch}
          >
            {busy
              ? "Opening…"
              : suggestions.length > 0
                ? "Review match"
                : "Check for matches"}
          </button>
        )}
        {fileUrl && isPdf && (
          <a
            className="btn btn-ghost btn-block"
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
          >
            Open original PDF
          </a>
        )}
        {fileUrl && !isPdf && (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            onClick={() => setViewerOpen(true)}
          >
            Open full size
          </button>
        )}
        {viewerOpen && fileUrl && !isPdf && (
          <ReceiptImageViewer
            src={fileUrl}
            downloadName={receipt.original_filename}
            onClose={closeViewer}
          />
        )}
        {receipt.match_status !== "matched" && (
          <button
            type="button"
            className="btn btn-danger btn-block"
            disabled={busy}
            onClick={remove}
          >
            Delete receipt
          </button>
        )}
      </div>

      <SiblingNav
        currentId={receipt.id}
        siblingIds={siblingIds}
        onGo={goSibling}
        busy={busy}
        compact
      />
      <BottomNav />
    </div>
  );
}
